import React, { useState, useEffect } from 'react';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const ACT_ICONS = {
  meeting: '👥', call: '📞', task: '✅', deadline: '⏰',
  email: '📧', lunch: '🍽️', default: '📌'
};

function formatDate(str) {
  if (!str) return '—';
  return str.substring(0, 10).split('-').reverse().join('.');
}

function formatDateTime(str) {
  if (!str) return '—';
  return str.substring(0, 16).replace('T', ' ').split('-').slice(0,2).join('.') + '.' + str.substring(0,4) + ' ' + str.substring(11,16);
}

export default function PipedriveTimeline({ dealId, dealUrl }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/pipedrive-timeline/${dealId}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('zba_admin_token')}` }
      });
      setData(await res.json());
    } catch (err) {
      setData({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !data) load();
  }, [open]);

  if (!dealId) return null;

  const allItems = data ? [
    ...(data.activities || []).map(a => ({ ...a, _type: 'activity', _date: a.dueDate })),
    ...(data.notes || []).map(n => ({ ...n, _type: 'note', _date: n.addTime?.substring(0, 10) })),
  ].sort((a, b) => new Date(b._date || 0) - new Date(a._date || 0)) : [];

  return (
    <div style={{ marginTop: '12px' }}>
      <button onClick={() => setOpen(!open)}
        style={{ padding: '6px 14px', background: open ? '#3E4954' : '#f1f5f9',
          color: open ? 'white' : '#3E4954', border: '1px solid #dde1e7', borderRadius: '8px',
          cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'system-ui',
          display: 'flex', alignItems: 'center', gap: '6px' }}>
        {open ? '▲' : '▼'} Pipeline-Verlauf
        {data && <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '100px', padding: '1px 6px' }}>
          {allItems.length}
        </span>}
      </button>

      {open && (
        <div style={{ marginTop: '12px', borderLeft: '2px solid #e2e8f0', paddingLeft: '16px' }}>
          {loading && <div style={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'system-ui' }}>⟳ Lade Verlauf…</div>}
          {data?.error && <div style={{ color: '#ef4444', fontSize: '12px', fontFamily: 'system-ui' }}>Fehler: {data.error}</div>}
          {!loading && allItems.length === 0 && <div style={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'system-ui' }}>Keine Einträge.</div>}

          {allItems.map((item, i) => (
            <div key={`${item._type}-${item.id}-${i}`} style={{
              position: 'relative', marginBottom: '16px', paddingBottom: '16px',
              borderBottom: i < allItems.length - 1 ? '1px solid #f1f5f9' : 'none',
            }}>
              {/* Timeline dot */}
              <div style={{
                position: 'absolute', left: '-21px', top: '2px',
                width: '10px', height: '10px', borderRadius: '50%',
                background: item._type === 'note' ? '#f59e0b' :
                  item.done ? '#10b981' : '#6366f1',
                border: '2px solid white',
              }} />

              {item._type === 'activity' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '14px' }}>{ACT_ICONS[item.actType] || ACT_ICONS.default}</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e2b38', fontFamily: 'system-ui' }}>
                      {item.subject}
                    </span>
                    {item.done && <span style={{ fontSize: '10px', background: '#d1fae5', color: '#065f46',
                      padding: '1px 6px', borderRadius: '100px', fontWeight: 700 }}>Erledigt</span>}
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'system-ui' }}>
                    {formatDate(item.dueDate)}{item.dueTime && ` um ${item.dueTime}`}
                    {item.userName && ` · ${item.userName}`}
                  </div>
                  {item.note && <div style={{ marginTop: '6px', fontSize: '12px', color: '#475569',
                    fontFamily: 'system-ui', background: '#f8fafc', padding: '8px 10px', borderRadius: '8px' }}>
                    {item.note}
                  </div>}
                </div>
              )}

              {item._type === 'note' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px' }}>📝</span>
                    <span style={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'system-ui' }}>
                      Notiz · {formatDate(item.addTime?.substring(0,10))}
                      {item.userName && ` · ${item.userName}`}
                      {item.pinnedToDeal && ' · 📌 Angepinnt'}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#1e2b38', fontFamily: 'system-ui',
                    background: '#fffbeb', border: '1px solid #fde68a', padding: '8px 12px', borderRadius: '8px',
                    whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.content) }} />
                </div>
              )}
            </div>
          ))}

          {dealUrl && (
            <a href={dealUrl} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: '12px', color: '#6366f1', textDecoration: 'none', fontFamily: 'system-ui',
                display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              In Pipedrive öffnen ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}
